import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

// Route mapping for automatic breadcrumb generation
const routeMap: Record<string, string> = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/services': 'Services',
  '/services/create': 'Create Service',
  '/analytics': 'Analytics',
  '/analytics/revenue': 'Revenue',
  '/analytics/usage': 'Usage',
  '/analytics/subscriptions': 'Subscriptions',
  '/payouts': 'Payouts',
  '/payouts/history': 'History',
  '/payouts/settings': 'Settings',
  '/my-account': 'My Account',
  '/my-account/profile': 'Profile',
  '/my-account/subscriptions': 'My Subscriptions',
  '/my-account/settings': 'Settings',
  'usage': 'Usage Details',
  'billing': 'Billing History',
};

const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', href: '/', icon: Home },
  ];

  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Handle dynamic routes
    if (segment.match(/^[a-f0-9-]{36}$/)) {
      // UUID pattern - likely an ID
      breadcrumbs.push({
        label: 'Details',
        href: index === segments.length - 1 ? undefined : currentPath,
      });
    } else if (segment === 'edit' && index > 0) {
      breadcrumbs.push({
        label: 'Edit',
        href: index === segments.length - 1 ? undefined : currentPath,
      });
    } else {
      const label = routeMap[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1);
      breadcrumbs.push({
        label,
        href: index === segments.length - 1 ? undefined : currentPath,
      });
    }
  });

  return breadcrumbs;
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
}) => {
  const location = useLocation();
  
  const breadcrumbItems = items || generateBreadcrumbs(location.pathname);

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav className={cn('flex items-center space-x-1 text-sm text-muted-foreground', className)}>
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const Icon = item.icon;

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
            )}
            
            {item.href && !isLast ? (
              <Link
                to={item.href}
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{item.label}</span>
              </Link>
            ) : (
              <span
                className={cn(
                  'flex items-center gap-1',
                  isLast ? 'text-foreground font-medium' : 'text-muted-foreground'
                )}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{item.label}</span>
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// Hook for custom breadcrumb management
export const useBreadcrumbs = () => {
  const [customBreadcrumbs, setCustomBreadcrumbs] = React.useState<BreadcrumbItem[]>([]);

  const setBreadcrumbs = React.useCallback((items: BreadcrumbItem[]) => {
    setCustomBreadcrumbs(items);
  }, []);

  const clearBreadcrumbs = React.useCallback(() => {
    setCustomBreadcrumbs([]);
  }, []);

  const addBreadcrumb = React.useCallback((item: BreadcrumbItem) => {
    setCustomBreadcrumbs(prev => [...prev, item]);
  }, []);

  return {
    breadcrumbs: customBreadcrumbs,
    setBreadcrumbs,
    clearBreadcrumbs,
    addBreadcrumb,
  };
};
